/**
 * Role-based access control component
 * Only renders children if the user has the required app role
 */

import React from 'react';
import { 
  Title1,
  Subtitle1,
  Body1,
  Caption1,
  Button,
  tokens,
  makeStyles
} from '@fluentui/react-components';
import { 
  ShieldError24Regular,
  Person24Regular,
  ContactCard24Regular,
  Warning24Regular
} from '@fluentui/react-icons';
import { useAuth } from '../auth/AuthProvider';
import { getAppConfig } from '../config/appConfig';

const useStyles = makeStyles({
  accessDeniedContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '60vh',
    padding: tokens.spacingVerticalXXL,
  },
  accessDeniedCard: {
    maxWidth: '500px',
    width: '100%',
    textAlign: 'center',
  },
  iconContainer: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: tokens.spacingVerticalL,
  },
  errorIcon: {
    fontSize: '64px',
    color: tokens.colorPaletteRedForeground1,
  },
  title: {
    marginBottom: tokens.spacingVerticalL,
    color: tokens.colorNeutralForeground1,
    lineHeight: tokens.lineHeightHero700,
    display: 'block',
    width: '100%',
    textAlign: 'center',
  },
  subtitle: {
    marginBottom: tokens.spacingVerticalL,
    color: tokens.colorNeutralForeground2,
    lineHeight: tokens.lineHeightBase400,
    wordWrap: 'break-word',
  },
  detailsSection: {
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: tokens.borderRadiusMedium,
    padding: tokens.spacingVerticalM,
    marginBottom: tokens.spacingVerticalL,
    textAlign: 'left',
  },
  detailRow: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    marginBottom: tokens.spacingVerticalS,
    '&:last-child': {
      marginBottom: '0',
    },
  },
  detailIcon: {
    color: tokens.colorNeutralForeground2,
  },
  detailLabel: {
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
    minWidth: '100px',
  },
  detailValue: {
    color: tokens.colorNeutralForeground2,
  },
  noRoles: {
    color: tokens.colorPaletteRedForeground1,
    fontStyle: 'italic',
  },
  contactMessage: {
    backgroundColor: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    padding: tokens.spacingVerticalM,
    marginBottom: tokens.spacingVerticalL,
  },
  refreshButton: {
    marginTop: tokens.spacingVerticalM,
  },
});

interface RoleGuardProps {
  requiredRole?: string;
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

export const RoleGuard: React.FC<RoleGuardProps> = ({
  requiredRole,
  children,
  fallback
}) => {
  const classes = useStyles();
  const { user, userClaims, isAuthenticated } = useAuth();
  const config = getAppConfig();

  // Use provided role or fall back to configured role
  const roleToCheck = requiredRole || config.auth.requiredAppRole;

  // If not authenticated, don't render anything (handled by parent auth check)
  if (!isAuthenticated || !user) {
    return null;
  }

  // Check if user has the required role
  // In Azure AD, app roles are included in the 'roles' claim of the JWT token
  const userRoles = userClaims?.roles || [];
  const hasRequiredRole = userRoles.includes(roleToCheck);

  // Debug logging for role checking
  console.log('[RoleGuard] Debug info:', {
    roleToCheck,
    userClaims,
    userRoles,
    hasRequiredRole,
    allClaimsKeys: userClaims ? Object.keys(userClaims) : 'null'
  });

  if (!hasRequiredRole) {
    if (fallback) {
      return <>{fallback}</>;
    }

    return (
      <div className={classes.accessDeniedContainer}>
        <div className={classes.accessDeniedCard}>
          {/* Error Icon */}
          <div className={classes.iconContainer}>
            <ShieldError24Regular className={classes.errorIcon} />
          </div>

          {/* Main Title */}
          <Title1 className={classes.title}>
            Access Denied
          </Title1>

          {/* Subtitle */}
          <Subtitle1 className={classes.subtitle}>
            You don't have the required permissions to access this application.
          </Subtitle1>

          {/* Role Details */}
          <div className={classes.detailsSection}>
            <div className={classes.detailRow}>
              <Warning24Regular className={classes.detailIcon} />
              <span className={classes.detailLabel}>Required Role:</span>
              <Caption1 className={classes.detailValue}>
                {roleToCheck}
              </Caption1>
            </div>
            
            <div className={classes.detailRow}>
              <Person24Regular className={classes.detailIcon} />
              <span className={classes.detailLabel}>Your Roles:</span>
              <Caption1 className={userRoles.length > 0 ? classes.detailValue : classes.noRoles}>
                {userRoles.length > 0 ? userRoles.join(', ') : 'None assigned'}
              </Caption1>
            </div>
          </div>

          {/* Contact Message */}
          <div className={classes.contactMessage}>
            <div className={classes.detailRow}>
              <ContactCard24Regular className={classes.detailIcon} />
              <Body1>
                Please contact your administrator to request the appropriate role assignment.
              </Body1>
            </div>
          </div>

          {/* Refresh Button */}
          <Button 
            appearance="primary"
            className={classes.refreshButton}
            onClick={() => window.location.reload()}
          >
            Refresh Page
          </Button>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

export default RoleGuard;
